import type { Prayer } from "./types";

export const prayer: Prayer = {
  slug: "prayer-before-work",
  title: "A prayer before work — for a job that is mostly ordinary",
  h1: "A prayer before work",
  description:
    "A prayer for the start of a working day, four passages on labour, and what work being worship does and does not mean.",
  targets: [
    "prayer before work",
    "prayer for the workday",
    "prayer before a shift",
    "prayer for my job",
  ],
  published: "2026-09-03",
  standfirst:
    "Most work is not a calling and does not feel like one. The passages about it are, usefully, addressed to slaves and farmers rather than to people with careers.",
  takeaways: [
    "Ask for the work itself to be established, not for it to feel meaningful. Psalm 90:17 asks twice for exactly that and never mentions how it feels.",
    "Colossians 3:23 was written to household slaves. It is not a productivity verse and using it to demand more of employees inverts it.",
    "One prayer for the person you find hardest at work will do more for your week than any prayer about the workload.",
  ],
  when: [
    "In the car park, before you go in.",
    "At the start of a shift you are dreading for a specific reason.",
    "Before the meeting where the decision gets made.",
    "On the Monday of a week you can already see the shape of.",
  ],
  prayer: [
    "God, here is the day. Most of it is not interesting and it still has to be done well.",
    "Establish the work of my hands — that is the phrase and I am using it deliberately, because I would like at least some of this to still matter on Friday.",
    "Give me a fair share of patience for the person I find hardest. You know which one. Let me not be the difficult one myself.",
    "Where I am tempted to cut a corner nobody would ever find, let me not, and let that stay unremarkable rather than becoming something I congratulate myself for.",
    "And let me stop at the end of it. The work will still be unfinished; it was unfinished when I arrived.",
  ],
  short:
    "God, establish the work of my hands today, and let me stop at the end of it.",
  honest: [
    "This prayer does not make a bad job good. Some work is genuinely damaging, and a prayer for a better attitude towards it is a way of asking somebody to endure what they should be leaving.",
    "It also does not promise success. Proverbs 16:3 is a proverb about how things generally go, not a guarantee about your particular plan, and reading it as one has disappointed a great many people who did commit their work.",
    "And work being worship does not mean more hours. The same tradition that dignified ordinary labour also invented the sabbath, and it put the sabbath in the commandments rather than in the suggestions.",
  ],
  verses: [
    {
      verse: {
        text: "Let the favor of the Lord our God be on us. Establish the work of our hands for us. Yes, establish the work of our hands.",
        ref: "Psalm 90:17",
      },
      note: "Asked twice, in a psalm that has spent fifteen verses on how short life is and how quickly people are forgotten. Establish means to make it stand — to give it a permanence the psalm has just finished saying nobody has. It is a request about the work outlasting the worker, not about the worker enjoying it.",
    },
    {
      verse: {
        text: "And whatever you do, work heartily, as for the Lord and not for men,",
        ref: "Colossians 3:23",
      },
      note: "Written to household slaves, in a passage addressed to them before it turns to their masters. That original audience is worth holding on to, because the verse is now most often quoted by employers at employees — which reverses it. To a person with no rights and no prospects, it says your work has an audience your owner cannot see.",
    },
    {
      verse: {
        text: "Whatever your hand finds to do, do it with your might; for there is no work, nor plan, nor knowledge, nor wisdom, in Sheol, where you are going.",
        ref: "Ecclesiastes 9:10",
      },
      note: "The reasoning is bleak and that is why it is bracing. Not do your best because it matters eternally, but do it now because later there is nothing. The verse before recommends eating your bread with joy, and the two belong together: work hard, eat well, neither of them because the universe owes you a result.",
    },
    {
      verse: {
        text: "Commit your deeds to Yahweh, and your plans shall succeed.",
        ref: "Proverbs 16:3",
      },
      note: "A proverb, which is a description of how life generally runs rather than a contract — the same book says a great deal about plans that fail. Read as a guarantee it will eventually be disproved by your own experience. Read as the wisdom that work handed over tends to go better than work gripped, it holds up.",
    },
  ],
  body: [
    {
      type: "h2",
      text: "Establish, not enjoy",
      id: "establish-not-enjoy",
    },
    {
      type: "p",
      text: "The most useful thing about Psalm 90:17 is what it does not ask for. Not fulfilment, not a sense of purpose, not that the day feel significant. That the work stand up — that something be there afterwards.",
    },
    {
      type: "p",
      text: "This is a considerably better fit for most jobs than the language of calling, which sets an expectation the majority of work has never met for the majority of people who have ever worked.",
    },
    {
      type: "h2",
      text: "One person, not the workload",
      id: "one-person-not-the-workload",
    },
    {
      type: "p",
      text: "Almost nobody leaves a job because of the amount of work. They leave because of a person, or an atmosphere one person sets. A prayer about the volume is aimed at the wrong target.",
    },
    {
      type: "note",
      text: "Pray for the specific person by name, and include the possibility that from their side you are the difficult one.",
    },
    {
      type: "h2",
      text: "And stop",
      id: "and-stop",
    },
    {
      type: "p",
      text: "The tradition that made ordinary work dignified is the same one that legislated a day off, and it put the day off in the commandments. Any spirituality of work that produces more hours has lost half the material.",
    },
    {
      type: "p",
      text: "In practice that means a real end to the day, which for most people now means an end to the phone rather than an end to the desk — the {sabbath problem} in its modern form.",
      links: {
        "sabbath problem": "/blog/digital-sabbath",
      },
    },
  ],
  faq: [
    {
      q: "What is a good prayer before starting work?",
      a: '"God, establish the work of my hands today, and let me stop at the end of it." Psalm 90:17 asks for the work to stand rather than for it to feel meaningful, which is a better fit for most jobs.',
    },
    {
      q: "Which Bible verse is about work?",
      a: "Colossians 3:23 is the best known, and it is worth remembering it was written to household slaves rather than to ambitious professionals. Ecclesiastes 9:10 is the bracing one.",
    },
    {
      q: "Should I pray for a promotion?",
      a: "You can ask for anything. Just notice that none of the passages about work promise advancement — they promise that the work has an audience, and they are markedly more interested in how you do it than in where it takes you.",
    },
  ],
  relatedPrayers: ["prayer-for-focus", "prayer-for-patience"],
  related: ["digital-sabbath", "morning-quiet-time-routine"],
  relatedTopics: ["bible-verses-about-time", "bible-verses-about-rest"],
  relatedPassages: ["philippians-4-13"],
};
